/**
 * Fund Info Tab Component
 *
 * Displays detailed fund information for the holding detail view:
 * - AMC (Asset Management Company)
 * - Expense ratio
 * - Exit load schedule
 * - AUM (Assets Under Management)
 * - Benchmark
 * - Fund manager
 * - Inception date
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINRCompact, formatDate, formatPercent } from "@/lib/utils";
import {
  Building2,
  Percent,
  DoorOpen,
  TrendingUp,
  Target,
  User,
  Calendar,
  Clock,
  AlertCircle,
} from "lucide-react";
import type { Fund } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund info data for the tab display
 * Extracted from the Fund type with additional optional fund manager field
 */
export interface FundInfoData {
  /** Asset Management Company name */
  amcName: string;
  /** Expense ratio as percentage (e.g., 0.45 for 0.45%) */
  expenseRatio: number;
  /** Exit load description (e.g., "1% if redeemed within 1 year") */
  exitLoad: string;
  /** Assets Under Management in crores */
  aum: number;
  /** Benchmark index name */
  benchmarkIndex: string;
  /** Fund manager name(s) - optional, may not always be available */
  fundManager?: string;
  /** Fund launch/inception date (ISO date string) */
  launchDate: string;
  /** Lock-in period in days (0 if none) */
  lockInPeriod?: number;
  /** Minimum SIP amount in INR */
  minSipAmount?: number;
}

/**
 * Props for the FundInfoTab component
 */
export interface FundInfoTabProps {
  /** Fund information data */
  data: FundInfoData;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format AUM value with appropriate suffix
 */
function formatAUM(aumInCrores: number): string {
  if (aumInCrores >= 100) {
    return `${aumInCrores.toLocaleString("en-IN")} Cr`;
  }
  return `${aumInCrores.toFixed(2)} Cr`;
}

/**
 * Calculate fund age from inception date
 */
function calculateFundAge(launchDate: string): string {
  const launch = new Date(launchDate);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - launch.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);

  if (years === 0) {
    return `${months} month${months !== 1 ? "s" : ""}`;
  }
  if (months === 0) {
    return `${years} year${years !== 1 ? "s" : ""}`;
  }
  return `${years}y ${months}m`;
}

/**
 * Format lock-in period for display
 */
function formatLockInPeriod(days: number): string {
  if (days === 0) return "None";
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""}`;
  if (days < 365) {
    const months = Math.round(days / 30);
    return `${months} month${months !== 1 ? "s" : ""}`;
  }
  const years = Math.round(days / 365);
  return `${years} year${years !== 1 ? "s" : ""}`;
}

// ============================================================================
// Sub-components
// ============================================================================

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
  valueClassName?: string;
  highlight?: boolean;
}

/**
 * Single row of fund information
 */
function InfoRow({
  icon,
  label,
  value,
  subValue,
  valueClassName,
  highlight,
}: InfoRowProps) {
  return (
    <div
      className={cn(
        "flex items-start justify-between py-3",
        "border-b border-border/50 last:border-b-0",
        highlight && "bg-muted/30 -mx-4 px-4 rounded-lg"
      )}
    >
      <div className="flex items-center gap-2.5 text-muted-foreground">
        <div className="flex-shrink-0">{icon}</div>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-right">
        <p className={cn("text-sm font-semibold", valueClassName)}>{value}</p>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>
        )}
      </div>
    </div>
  );
}

interface InfoSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Section grouping for fund information
 */
function InfoSection({ title, children, className }: InfoSectionProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        {title}
      </h4>
      <div className="divide-y divide-border/50">{children}</div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Fund Info Tab
 *
 * Displays comprehensive fund information in a structured layout with icons
 * and clear visual hierarchy.
 *
 * @example
 * ```tsx
 * <FundInfoTab
 *   data={{
 *     amcName: "PPFAS Mutual Fund",
 *     expenseRatio: 0.75,
 *     exitLoad: "1% if redeemed within 365 days",
 *     aum: 42567,
 *     benchmarkIndex: "NIFTY 500 TRI",
 *     fundManager: "Rajeev Thakkar",
 *     launchDate: "2013-05-24",
 *     lockInPeriod: 0,
 *     minSipAmount: 1000,
 *   }}
 * />
 * ```
 */
export function FundInfoTab({ data, className }: FundInfoTabProps) {
  const fundAge = calculateFundAge(data.launchDate);

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">Fund Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* AMC & Management Section */}
        <InfoSection title="Fund House">
          <InfoRow
            icon={<Building2 className="h-4 w-4" />}
            label="AMC"
            value={data.amcName}
          />
          {data.fundManager && (
            <InfoRow
              icon={<User className="h-4 w-4" />}
              label="Fund Manager"
              value={data.fundManager}
            />
          )}
        </InfoSection>

        {/* Performance Benchmarks Section */}
        <InfoSection title="Benchmarks">
          <InfoRow
            icon={<Target className="h-4 w-4" />}
            label="Benchmark Index"
            value={data.benchmarkIndex}
          />
          <InfoRow
            icon={<TrendingUp className="h-4 w-4" />}
            label="AUM"
            value={`₹${formatAUM(data.aum)}`}
            subValue="Assets Under Management"
          />
        </InfoSection>

        {/* Costs & Fees Section */}
        <InfoSection title="Costs & Fees">
          <InfoRow
            icon={<Percent className="h-4 w-4" />}
            label="Expense Ratio"
            value={`${data.expenseRatio.toFixed(2)}%`}
            valueClassName={
              data.expenseRatio <= 0.5
                ? "text-green-600"
                : data.expenseRatio <= 1.0
                  ? "text-amber-600"
                  : "text-red-600"
            }
          />
          <InfoRow
            icon={<DoorOpen className="h-4 w-4" />}
            label="Exit Load"
            value={data.exitLoad || "Nil"}
            highlight={data.exitLoad !== "Nil" && data.exitLoad !== ""}
          />
        </InfoSection>

        {/* Fund Details Section */}
        <InfoSection title="Fund Details">
          <InfoRow
            icon={<Calendar className="h-4 w-4" />}
            label="Inception Date"
            value={formatDate(data.launchDate)}
            subValue={`Fund age: ${fundAge}`}
          />
          {data.lockInPeriod !== undefined && data.lockInPeriod > 0 && (
            <InfoRow
              icon={<Clock className="h-4 w-4" />}
              label="Lock-in Period"
              value={formatLockInPeriod(data.lockInPeriod)}
              highlight
            />
          )}
          {data.minSipAmount !== undefined && data.minSipAmount > 0 && (
            <InfoRow
              icon={<AlertCircle className="h-4 w-4" />}
              label="Min. SIP Amount"
              value={formatINRCompact(data.minSipAmount)}
            />
          )}
        </InfoSection>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for FundInfoTab
 */
export function FundInfoTabSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-2">
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section 1 */}
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2.5">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>

        {/* Section 2 */}
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2.5">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="text-right space-y-1">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>

        {/* Section 3 */}
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2.5">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>

        {/* Section 4 */}
        <div className="space-y-2">
          <Skeleton className="h-3 w-24" />
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2.5">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="text-right space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Utility: Convert Fund to FundInfoData
// ============================================================================

/**
 * Helper to extract FundInfoData from a Fund object
 * Useful when you have a full Fund and need to pass to FundInfoTab
 */
export function extractFundInfoData(
  fund: Fund,
  fundManager?: string
): FundInfoData {
  return {
    amcName: fund.amcName,
    expenseRatio: fund.expenseRatio,
    exitLoad: fund.exitLoad,
    aum: fund.aum,
    benchmarkIndex: fund.benchmarkIndex,
    fundManager,
    launchDate: fund.launchDate,
    lockInPeriod: fund.lockInPeriod,
    minSipAmount: fund.minSipAmount,
  };
}

export default FundInfoTab;
